import { addSeconds, differenceInSeconds, format, formatISO, parseISO } from 'date-fns';
import L from 'leaflet';
import 'leaflet-routing-machine';

import { VasttrafikApiClient } from './VasttrafikApiClient';
import {
    type JourneysGetRequest,
    type VTApiPlaneraResaWebV4ModelsJourneyDetailsArrivalAccessLinkApiModel,
    type VTApiPlaneraResaWebV4ModelsJourneyDetailsDepartureAccessLinkApiModel,
    type VTApiPlaneraResaWebV4ModelsJourneyDetailsDestinationLinkApiModel,
    type VTApiPlaneraResaWebV4ModelsJourneyDetailsJourneyDetailsApiModel,
    type VTApiPlaneraResaWebV4ModelsJourneyDetailsTripLegDetailsApiModel,
    type VTApiPlaneraResaWebV4ModelsJourneysArrivalAccessLinkApiModel,
    type VTApiPlaneraResaWebV4ModelsJourneysDepartureAccessLinkApiModel,
    type VTApiPlaneraResaWebV4ModelsJourneysDestinationLinkApiModel,
    type VTApiPlaneraResaWebV4ModelsJourneysJourneyApiModel,
    type VTApiPlaneraResaWebV4ModelsJourneysTripLegApiModel,
} from './VasttrafikApiClient.types';

type Route = L.Routing.IRoute & {
    inputWaypoints: L.Routing.Waypoint[];
    legs: RouteLeg[];
    summary: L.Routing.IRouteSummary & {
        departureTime: string;
        arrivalTime: string;
    };
};

type RouteLeg = {
    name?: string;
    coordinates?: L.LatLng[];
    instructions?: L.Routing.IInstruction[];
    origin?: {
        name?: string;
        dateTime: string;
    };
    destination?: {
        name?: string;
        dateTime: string;
    };
    totalTime?: number;
    type: string;
};

type AccessLinkApiModel = (
    | VTApiPlaneraResaWebV4ModelsJourneysDepartureAccessLinkApiModel
    | VTApiPlaneraResaWebV4ModelsJourneysArrivalAccessLinkApiModel
    | VTApiPlaneraResaWebV4ModelsJourneysDestinationLinkApiModel
) &
    (
        | VTApiPlaneraResaWebV4ModelsJourneyDetailsDepartureAccessLinkApiModel
        | VTApiPlaneraResaWebV4ModelsJourneyDetailsArrivalAccessLinkApiModel
        | VTApiPlaneraResaWebV4ModelsJourneyDetailsDestinationLinkApiModel
    );

type RoutingOptions = {
    date?: string;
    time?: string;
};

export class VasttrafikRouter implements L.Routing.IRouter {
    private _vasttrafikApiClient: VasttrafikApiClient;

    options: RoutingOptions = {};

    constructor() {
        for (const i in this.options) {
            if (Object.hasOwn(this.options, i)) {
                this._setOptionFromCookie(i);
            }
        }

        this._vasttrafikApiClient = new VasttrafikApiClient('https://ext-api.vasttrafik.se/pr/v4');
    }

    setOption(name: string, value: unknown): void {
        this.options[name] = value;
    }

    async route(waypoints: L.Routing.Waypoint[], callback: (error?: L.Routing.IError, routes?: Route[]) => void, context?: object): Promise<void> {
        // Calculate routes waypoint by waypoint
        const sectionsPromises = [];
        for (let i = 0; i < waypoints.length - 1; i++) {
            const origin = waypoints[i];
            const destination = waypoints[i + 1];

            const routesPromise = this._route(origin, destination, i === 0 ? 2 : 1);

            sectionsPromises.push(routesPromise);
        }

        try {
            const sections = await Promise.all(sectionsPromises);

            // Aggregate routes
            const aggregatedRoutes = this._createRouteCombinations(sections);

            // Sort routes by arrival time
            aggregatedRoutes.sort((route1, route2) => {
                const arrivalTime1 = parseISO(route1.summary.arrivalTime);
                const arrivalTime2 = parseISO(route2.summary.arrivalTime);

                return arrivalTime1.getTime() - arrivalTime2.getTime();
            });

            callback.call(context, null, aggregatedRoutes);
        } catch {
            callback.call(context, 'Något gick fel, testa en annan sökning eller försök igen senare.', null);
        }
    }

    // Returns a list of all possible combination of routes where each combined route consists of one route from each section
    private _createRouteCombinations(sections: Route[][]): Route[] {
        if (sections.length === 0) {
            return [];
        }

        const routes = sections[0];

        if (sections.length === 1) {
            return routes;
        }

        const combinedRoutes = [];

        for (const route of routes) {
            const combinedRoute = this._createRouteCombinations(sections.slice(1));
            combinedRoute.forEach((r) => {
                combinedRoutes.push(this._mergeRoutes(route, r));
            });
        }

        return combinedRoutes;
    }

    private _mergeRoutes(route1: Route, route2: Route): Route {
        return {
            inputWaypoints: [...route1.inputWaypoints, route2.inputWaypoints.slice(-1)[0]],
            legs: route1.legs.concat(route2.legs),
            instructions: route1.instructions.concat(route2.instructions),
            coordinates: route1.coordinates.concat(route2.coordinates),
            summary: {
                departureTime: route1.summary.departureTime,
                arrivalTime: addSeconds(parseISO(route1.summary.arrivalTime), route2.summary.totalTime).toISOString(),
                totalDistance: route1.summary.totalDistance + route2.summary.totalDistance,
                totalTime: route1.summary.totalTime + route2.summary.totalTime,
            },
        };
    }

    async _route(origin: L.Routing.Waypoint, destination: L.Routing.Waypoint, maxAlternatives: number): Promise<Route[]> {
        const originLatLng = origin.latLng;
        const destinationLatLng = destination.latLng;
        const request: JourneysGetRequest = {
            limit: maxAlternatives,
            originLatitude: originLatLng.lat,
            originLongitude: originLatLng.lng,
            destinationLatitude: destinationLatLng.lat,
            destinationLongitude: destinationLatLng.lng,
            dateTime: this._getDepartureTime(),
            dateTimeRelatesTo: 'departure',
            includeNearbyStopAreas: true,
            originWalk: '0',
            destWalk: '0',
            originBike: '1,0,1000000',
            destBike: '1,0,10000000',
            totalBike: '1,0,10000000',
            originCar: '0',
            originPark: '0',
            interchangeDurationInMinutes: 1,
            transportModes: ['bike', 'ferry'],
        };

        const journeysResponse = await this._vasttrafikApiClient.getJourneys(request);

        // Error handling, todo...

        // Loop through the first two journeys
        const routes = await Promise.all(
            journeysResponse.results.slice(0, maxAlternatives).map(async (journey) => {
                const journeyDetails = await this._vasttrafikApiClient.getJourneyDetails({
                    detailsReference: journey.detailsReference,
                    includes: ['links', 'triplegcoordinates'],
                });

                // Error handling, todo...

                const route = this._getRoute(journey, journeyDetails);
                route.waypoints = [originLatLng, destinationLatLng];
                route.inputWaypoints = [origin, destination];

                return route as Route;
            }),
        );

        return routes.slice(0, maxAlternatives);
    }

    _getDepartureTime(): string {
        const date = this.options.date || format(new Date(), 'yyyy-MM-dd');
        const time = (this.options.time && this.options.time + ':00') || format(new Date(), 'HH:mm:ss');
        return formatISO(new Date(date + 'T' + time));
    }

    _getRoute(
        journey: VTApiPlaneraResaWebV4ModelsJourneysJourneyApiModel,
        journeyDetails: VTApiPlaneraResaWebV4ModelsJourneyDetailsJourneyDetailsApiModel,
    ): Partial<Route> {
        const departureAccessLink = journey.arrivalAccessLink && { ...journey.departureAccessLink, ...journeyDetails.departureAccessLink };
        const tripLegs = journey.tripLegs && { ...journey.tripLegs[0], ...journeyDetails.tripLegs[0] };
        const arrivalAccessLink = journey.arrivalAccessLink && { ...journey.arrivalAccessLink, ...journeyDetails.arrivalAccessLink };
        const destinationLink = journey.destinationLink && { ...journey.destinationLink, ...journeyDetails.destinationLink };

        let legs: RouteLeg[];
        if (destinationLink) {
            legs = [this._getBikeLeg(destinationLink)];
        } else {
            const departureLeg = this._getBikeLeg(departureAccessLink);
            const ferryLeg = this._getTripLeg(tripLegs);
            const arrivalLeg = this._getBikeLeg(arrivalAccessLink);

            legs = [departureLeg, ferryLeg, arrivalLeg].filter((leg) => leg !== null);
        }

        const coordinates = legs.flatMap((leg) => leg.coordinates);
        const instructions = legs.flatMap((leg) => leg.instructions);

        return {
            coordinates: coordinates,
            instructions: instructions,
            legs,
            summary: {
                departureTime: legs[0].origin.dateTime,
                arrivalTime: legs[legs.length - 1].destination.dateTime,
                totalDistance: instructions.reduce((total, leg) => total + leg.distance, 0),
                totalTime: legs.reduce((total, leg) => total + leg.totalTime, 0),
            },
        };
    }

    _getBikeLeg(leg: AccessLinkApiModel): RouteLeg {
        const coordinates = leg.linkCoordinates.map((linkCoordinate) => {
            const latLng = new L.LatLng(linkCoordinate.latitude, linkCoordinate.longitude);
            latLng.alt = linkCoordinate.elevation;
            return latLng;
        });
        const instructions: L.Routing.IInstruction[] = leg.segments.map((segment) => {
            return {
                distance: segment.distanceInMeters,
                text: segment.maneuverDescription,
                time: null,
            };
        });

        const routeLeg: RouteLeg = {
            coordinates,
            instructions,
            origin: {
                dateTime: leg.origin.estimatedOtherwisePlannedTime,
            },
            destination: {
                dateTime: leg.destination.estimatedOtherwisePlannedTime,
            },
            type: leg.transportMode,
            totalTime: differenceInSeconds(
                parseISO(leg.destination.estimatedOtherwisePlannedTime),
                parseISO(leg.origin.estimatedOtherwisePlannedTime),
            ),
        };

        return routeLeg;
    }

    _getTripLeg(leg: VTApiPlaneraResaWebV4ModelsJourneysTripLegApiModel & VTApiPlaneraResaWebV4ModelsJourneyDetailsTripLegDetailsApiModel): RouteLeg {
        const coordinates = leg.tripLegCoordinates.map((coordinate) => {
            const latLng = new L.LatLng(coordinate.latitude, coordinate.longitude);
            latLng.alt = 0;
            return latLng;
        });
        const isFree = leg.notes.some((note) => note.text.indexOf('avgiftsfri') !== -1);
        const departureTime = parseISO(leg.estimatedOtherwisePlannedDepartureTime);
        const arrivalTime = parseISO(leg.estimatedOtherwisePlannedArrivalTime);
        const totalTime = differenceInSeconds(arrivalTime, departureTime);
        const name = leg.serviceJourney.line.name + (isFree ? ', gratisresa' : ', betalresa');
        const instructions = [
            {
                distance: 0,
                text: name + ' (avgång: ' + format(departureTime, 'HH.mm') + ', restid: ' + Math.round(totalTime / 60) + ' minuter)',
                time: totalTime,
            },
        ];

        return {
            name,
            coordinates,
            instructions,
            origin: {
                name: leg.origin.stopPoint.name,
                dateTime: leg.origin.estimatedOtherwisePlannedTime,
            },
            destination: {
                name: leg.destination.stopPoint.name,
                dateTime: leg.destination.estimatedOtherwisePlannedTime,
            },
            totalTime,
            type: leg.serviceJourney.line.transportMode,
        };
    }

    _setOptionFromCookie(option) {
        const cookie = document.cookie.replace(new RegExp('(?:(?:^|.*;\\s*)' + option + '\\s*\\=\\s*([^;]*).*$)|^.*$'), '$1');

        if (cookie) {
            this.options[option] = cookie;
        }
    }
}
